import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { defineMessages, useIntl } from 'react-intl';

import { generateSlug } from 'src/common/generateSlug';
import { useConfig } from 'src/common/hooks';
import { useCountryData } from 'src/common/hooks/useCountryData';
import { usePageNumberTranslation } from 'src/common/hooks/usePageNumberTranslation';
import { LocalizedCityNamesById } from 'src/common/messages/city';
import { ROUTES } from 'src/common/routes';
import GlintsContainer from 'src/components/GlintsContainer';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { AnchorPagination } from 'src/components/Pagination/AnchorPagination';
import { createUrlSlug } from 'src/modules/Opportunities/utils/createUrlSlug';
import {
  GetActiveJobsArg,
  GetCitiesWithActiveJobsData,
  queryGetCitiesWithActiveJobs,
} from 'src/modules/Seo/graphql';

import * as S from './JobsIndexPage.sc';

export const JobsByLocationPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const router = useRouter();
  const intl = useIntl();
  const { LANG: language } = useConfig();
  const { countryCode, englishCountryName } = useCountryData();

  const currentPage = Number(router.query.page) || 1;
  const pageTranslation = usePageNumberTranslation(currentPage);

  const { data, error } = useQuery<
    GetCitiesWithActiveJobsData,
    GetActiveJobsArg
  >(queryGetCitiesWithActiveJobs, {
    variables: {
      countryCode,
      pageSize: 60,
      currentPage,
    },
  });

  useEffect(function redirectTo500Page() {
    const pageOutOfRange =
      data && currentPage > data.getCitiesWithActiveJobs.pageInfo.totalPage;

    const positiveTotalCount =
      data?.getCitiesWithActiveJobs.pageInfo.totalCount > 0;

    if (error) {
      router.replace('/500');
    } else if (positiveTotalCount && pageOutOfRange) {
      router.push({ pathname: router.pathname }, undefined, {
        shallow: true,
      });
    }
  });

  return (
    <>
      <MetadataRenderer
        title={intl.formatMessage(messages.title, {
          country: englishCountryName,
          pageTranslation,
        })}
        description={intl.formatMessage(messages.description, {
          country: englishCountryName,
        })}
        glintsTrackingName="explore"
      />
      <GlintsContainer>
        <S.Header>
          {intl.formatMessage(messages.h1, { country: englishCountryName })}
        </S.Header>
        {data && (
          <>
            <S.ListContainer>
              <S.LinkList>
                {data.getCitiesWithActiveJobs.cities.map(({ City }) => {
                  const cityNameSlug = generateSlug(City.name);
                  const urlSlug = createUrlSlug({
                    slug: cityNameSlug,
                    countryCode,
                    language,
                  });
                  const cityId = parseInt(City.id);
                  let cityName = City.name;
                  if (LocalizedCityNamesById[cityId]) {
                    cityName = intl.formatMessage(
                      LocalizedCityNamesById[cityId]
                    );
                  }
                  return (
                    <S.LinkItem key={City.id}>
                      <Link
                        href={`/${ROUTES.opportunitiesJobsExplore}`}
                        as={`/location/${urlSlug}/${City.id}`}
                      >
                        {intl.formatMessage(messages.anchor, {
                          city: cityName,
                        })}
                      </Link>
                    </S.LinkItem>
                  );
                })}
              </S.LinkList>
            </S.ListContainer>
            {data.getCitiesWithActiveJobs.pageInfo.totalPage > 1 && (
              <S.PaginationContainer>
                <AnchorPagination
                  totalPage={data.getCitiesWithActiveJobs.pageInfo.totalPage}
                  currentPage={currentPage}
                  basePathname="/browse/location"
                />
              </S.PaginationContainer>
            )}
          </>
        )}
      </GlintsContainer>
    </>
  );
};

const messages = defineMessages({
  title: {
    id: 'jobs-by-location-page_meta_title',
    defaultMessage:
      'Browse Jobs by Locations in {country} - Jobs Near You {pageTranslation}| Glints',
  },
  description: {
    id: 'jobs-by-location-page_meta_description',
    defaultMessage:
      'Browse job vacancies near you! Discover jobs opportunities in various location in {country} with Glints: Career Discovery without Limits',
  },
  h1: {
    id: 'jobs-by-location-page_header',
    defaultMessage: 'Browse Jobs In {country} by City / Location ',
  },
  anchor: {
    id: 'jobs-by-location-page_anchor',
    defaultMessage: '{city} Jobs',
  },
});
